<template>
  <div id="app" :class="themeClass">
    <main-site />
    <logo class="app__logo" v-if="showLogo" />
    <alert-socket />
    <visibility-checker />
    <title-changer />
    <message-notifications />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import jwt from 'jsonwebtoken';
import Logo from '@/assets/images/logos/sellia-logo.svg';
import errorHandler from '@/utils/error.handler';
import { onLogout } from '@/vue-apollo';
import apolloProvider from '@/apollo';

export default {
  components: {
    MainSite: () => import('@/views/MainSite.vue'),
    AlertSocket: () => import('@/components/common/AlertSocket.vue'),
    MessageNotifications: () => import('@/components/common/MessageNotifications.vue'),
    TitleChanger: () => import('@/components/common/TitleChanger.vue'),
    VisibilityChecker: () => import('@/components/common/VisibilityChecker.vue'),
    Logo,
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    ...mapState({
      token: (state) => state.token,
    }),
    ...mapGetters([
      'theme',
      'userInfo',
    ]),
    showLogo() {
      return this.windowWidth > 1800;
    },
    themeClass() {
      return this.theme === 'light' ? 'theme-light' : 'theme-dark';
    },
    status() {
      return this.userInfo.status;
    },
  },
  created() {
    this.getAuth();
  },
  mounted() {
    this.initSounds();
    this.initVisibilityListener();
    this.askNotificationPermission();

    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
  watch: {
    status(newVal) {
      if (newVal === 'offline') {
        this.redirectWithError('Expired token');
      }
    },
  },
  methods: {
    ...mapMutations([
      'setUser',
      'setToken',
      'setLanguage',
    ]),
    getAuth() {
      if (this.$route.query.data) {
        this.setToken(this.$route.query.data);
        this.checkToken();
        this.setLanguage(this.$moment.locale());
        this.$router.replace('/');
      } else if (!this.token) {
        this.redirectWithError('Missing token');
      } else {
        this.checkToken();
      }
    },
    checkToken() {
      const user = jwt.decode(this.token);

      if (!user || !user.id || !user.exp || !user.role) {
        return this.redirectWithError('Bad request');
      }

      if (user.role !== 'agent') {
        return this.redirectWithError('Wrong role');
      }

      const expires = this.$moment.unix(user.exp);

      if (!expires.isValid()) {
        return this.redirectWithError('Invalid token');
      }

      const today = this.$moment(new Date()).format();

      if (expires.format() <= today) {
        return this.redirectWithError('Expired token');
      }

      return this.setUser(user);
    },
    redirectWithError(err) {
      errorHandler.logErrors(err);
      onLogout(apolloProvider.defaultClient);
    },
  },
};
</script>

<style lang="scss">
@import "~styles/_app.scss";
</style>
